import React, { useEffect, useState } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import CheckboxGroup from '@/UIlibrary/CheckboxGroup/CheckboxGroup';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import {
	FillEvaluationMatrixInput,
	FillEvaluationMatrixMutation,
	ApplicantPaymentExperienceI,
	ApplicantPaymentExperienceIi,
	EconomicSolvencyI,
	EconomicSolvencyIi,
	useFillEvaluationMatrixMutation,
	FamilyMemberPaymentExperienceIi,
	RecoveryTimeInYears,
	Request,
	Maybe
} from '@/graphql/__generated__/van-credits';
import { BRAND_COLORS, TEXT_COLORS } from '@/UIlibrary/globalTheme';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Info, AlertTriangle } from 'react-feather';
import Modal from '@/UIlibrary/Modal/Modal';
import Button from '@/UIlibrary/Button/Button';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import TextSectionWithFormula from './TextSectionWithFormula';

interface EvaluationMatrixProps {
	requestData?: Maybe<Request> | undefined;
	requestId: string;
	refetchData?: () => Promise<any>;
	inCreditCover?: boolean;
}

const EvaluationMatrix: React.FC<EvaluationMatrixProps> = ({
	requestData,
	requestId,
	refetchData,
	inCreditCover = false
}) => {
	const t = useTranslations('VanCreditRequestSimulation');
	const [openInformationModal, setOpenInformationModal] = useState<boolean>(false);
	const [informationModalContent, setInformationModalContent] =
		useState<string>('EconomicSolvency');

	const vanCredits = useApolloClient(SCHEMAS.VAN_CREDITS);

	const economicSolvencyIi = requestData?.economicSolvencyIi || '-';
	const recoveryTimeInYears = requestData?.recoveryTimeInYears || '-';

	const [fillEvaluationMatrix, { loading }] = useFillEvaluationMatrixMutation({
		client: vanCredits,
		onCompleted: async (data: FillEvaluationMatrixMutation) => {
			const fillEvaluationMatrixResults = data.fillEvaluationMatrix;
			if (fillEvaluationMatrixResults?.successful) {
				logger.success('Matrix filled successfully', fillEvaluationMatrixResults);
				toast.success(t('MatrixFilledSuccessfully'));
				if (refetchData) {
					await refetchData();
				}
			}
			if (
				fillEvaluationMatrixResults?.mutationErrors &&
				fillEvaluationMatrixResults.mutationErrors.length > 0
			) {
				fillEvaluationMatrixResults.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				toast.error(t('ErrorFillingEvaluationMatrix'));
			}
		},
		onError: (error) => {
			logger.error('Error filling matriz', error);
			toast.error(t('ErrorFillingEvaluationMatrix'));
		}
	});

	const [paymentExperienceOneSelectedValue, setPaymentExperienceOneSelectedValue] =
		useState<string>(requestData?.evaluationMatrix?.applicantPaymentExperienceI || '');

	const [
		paymentExperienceTwoSolicitorSelectedValue,
		setPaymentExperienceTwoSolicitorSelectedValue
	] = useState<string>(requestData?.evaluationMatrix?.applicantPaymentExperienceIi || '');

	const [paymentExperienceTwoFamilySelectedValue, setPaymentExperienceTwoFamilySelectedValue] =
		useState<string>(requestData?.evaluationMatrix?.familyMemberPaymentExperienceIi || '');

	const [economicSolvencyISelectedValue, setEconomicSolvencyISelectedValue] = useState<string>(
		requestData?.evaluationMatrix?.economicSolvencyI || ''
	);

	const [economicSolvencyIiSelectedValue, setEconomicSolvencyIiSelectedValue] = useState<string>(
		requestData?.evaluationMatrix?.economicSolvencyIi || ''
	);

	const [recoveryTimeInYearsSelectedValue, setRecoveryTimeInYearsSelectedValue] = useState<string>(
		requestData?.evaluationMatrix?.recoveryTimeInYears || ''
	);

	useEffect(() => {
		const getInitialEconomicSolvencyIiValue = () => {
			if (requestData?.evaluationMatrix?.economicSolvencyIi) {
				return requestData.evaluationMatrix.economicSolvencyIi;
			}

			if (!economicSolvencyIi) {
				return '';
			}

			const solvency = parseFloat(economicSolvencyIi);
			if (solvency > 2) {
				return EconomicSolvencyIi.MORE_TWO_POINTS;
			} else if (solvency == 2) {
				return EconomicSolvencyIi.EQUAL_TWO_POINTS;
			} else if (solvency > 1 && solvency < 2) {
				return EconomicSolvencyIi.BETWEEN_ONE_POINT;
			} else if (solvency < 1) {
				return EconomicSolvencyIi.LESS_ONE_POINT;
			} else {
				return '';
			}
		};

		setPaymentExperienceOneSelectedValue(
			requestData?.evaluationMatrix?.applicantPaymentExperienceI || ''
		);
		setPaymentExperienceTwoSolicitorSelectedValue(
			requestData?.evaluationMatrix?.applicantPaymentExperienceIi || ''
		);
		setPaymentExperienceTwoFamilySelectedValue(
			requestData?.evaluationMatrix?.familyMemberPaymentExperienceIi || ''
		);
		setEconomicSolvencyISelectedValue(requestData?.evaluationMatrix?.economicSolvencyI || '');
		setEconomicSolvencyIiSelectedValue(getInitialEconomicSolvencyIiValue());
		setRecoveryTimeInYearsSelectedValue(requestData?.evaluationMatrix?.recoveryTimeInYears || '');
	}, [economicSolvencyIi, requestData]);

	const headers: string[] = ['Indicators', 'SourceFormula', 'Params', '', '', '', 'Result'];

	const paymentExperienceOneRow: Record<string, string | null>[] = [
		{ label: 'PaymentExperienceOne', points: null },
		{ label: 'CreditBureau', points: null },
		{ label: 'NoDelays', points: 'ThirtyPts' },
		{ label: 'OneToSixMonths', points: 'FifteenPts' },
		{ label: 'SixPlusMonths', points: 'FivePts' },
		{ label: 'NoData', points: 'ZeroPts' },
		{ label: '', points: null }
	];

	const paymentExperienceTwoRow: Record<string, string | null>[] = [
		{ label: 'PaymentExperienceTwo', points: null },
		{ label: 'Fincluyente', points: null },
		{ label: 'LessOr30Days', points: 'ZeroPts' },
		{ label: 'ThirtyOneToSixty', points: 'MinusThreePts' },
		{ label: 'SixtyOneToNinety', points: 'MinusFourPts' },
		{ label: 'NinetyOneToOneEighty', points: 'MinusFivePts' },
		{ label: '', points: null }
	];

	const economicSolvencyIRow: Record<string, string | null>[] = [
		{ label: 'EconomicSolvencyOne', points: null },
		{ label: 'Calculation', points: null },
		{ label: 'LessThanPointThree', points: 'TenPts' },
		{ label: 'LessThanPointFour', points: 'SevenPts' },
		{ label: 'LessThanPointFiveMoreThanFour', points: 'FivePts' },
		{ label: 'MoreThanPointFive', points: 'TwoPts' },
		{ label: '', points: null }
	];

	const economicSolvencyIiRow: Record<string, string | null>[] = [
		{ label: 'EconomicSolvency', points: null },
		{ label: 'Calculation', points: null },
		{ label: 'MoreThanTwo', points: 'TwentyPts' },
		{ label: 'EqualToTwo', points: 'FourteenPts' },
		{ label: 'BetweenOneAndTwo', points: 'TenPts' },
		{ label: 'LessThanOne', points: 'ZeroPts' },
		{ label: '', points: null }
	];

	const recoveryTimeInYearsRow: Record<string, string | null>[] = [
		{ label: 'RecuperationTime', points: null },
		{ label: 'Calculation', points: null },
		{ label: 'LessThanFive', points: 'FifteenPts' },
		{ label: 'LessThanSix', points: 'ThirteenPts' },
		{ label: 'MoreThanSix', points: 'TenPts' },
		{ label: 'MoreThanSeven', points: 'FivePts' },
		{ label: '', points: null }
	];

	const paymentExperienceOneOptions = [
		{ value: ApplicantPaymentExperienceI.NO_DELAY, label: '' },
		{ value: ApplicantPaymentExperienceI.ONE_MONTH, label: '' },
		{ value: ApplicantPaymentExperienceI.SIX_MONTHS, label: '' },
		{ value: ApplicantPaymentExperienceI.NO_DATA, label: '' }
	];

	const paymentExperienceOnePointsMap = {
		[ApplicantPaymentExperienceI.NO_DELAY]: '30',
		[ApplicantPaymentExperienceI.ONE_MONTH]: '15',
		[ApplicantPaymentExperienceI.SIX_MONTHS]: '5',
		[ApplicantPaymentExperienceI.NO_DATA]: '0'
	};

	const paymentExperienceTwoOptions = [
		{ value: ApplicantPaymentExperienceIi.ONE_DAY, label: '' },
		{ value: ApplicantPaymentExperienceIi.THIRTY_ONE_DAYS, label: '' },
		{ value: ApplicantPaymentExperienceIi.SIXTY_ONE_DAYS, label: '' },
		{ value: ApplicantPaymentExperienceIi.NINETY_ONE_DAYS, label: '' }
	];

	const paymentExperienceTwoPointsMap = {
		[ApplicantPaymentExperienceIi.ONE_DAY]: '0',
		[ApplicantPaymentExperienceIi.THIRTY_ONE_DAYS]: '-3',
		[ApplicantPaymentExperienceIi.SIXTY_ONE_DAYS]: '-4',
		[ApplicantPaymentExperienceIi.NINETY_ONE_DAYS]: '-5'
	};

	const economicSolvencyIOptions = [
		{ value: EconomicSolvencyI.LESS_THREE_POINTS, label: '' },
		{ value: EconomicSolvencyI.LESS_FOUR_POINTS, label: '' },
		{ value: EconomicSolvencyI.LESS_FIVE_POINTS, label: '' },
		{ value: EconomicSolvencyI.MORE_FIVE_POINTS, label: '' }
	];

	const economicSolvencyIPointsMap = {
		[EconomicSolvencyI.LESS_THREE_POINTS]: '10',
		[EconomicSolvencyI.LESS_FOUR_POINTS]: '7',
		[EconomicSolvencyI.LESS_FIVE_POINTS]: '5',
		[EconomicSolvencyI.MORE_FIVE_POINTS]: '2'
	};

	const economicSolvencyIiOptions = [
		{ value: EconomicSolvencyIi.MORE_TWO_POINTS, label: '' },
		{ value: EconomicSolvencyIi.EQUAL_TWO_POINTS, label: '' },
		{ value: EconomicSolvencyIi.BETWEEN_ONE_POINT, label: '' },
		{ value: EconomicSolvencyIi.LESS_ONE_POINT, label: '' }
	];

	const economicSolvencyIiPointsMap = {
		[EconomicSolvencyIi.MORE_TWO_POINTS]: '20',
		[EconomicSolvencyIi.EQUAL_TWO_POINTS]: '14',
		[EconomicSolvencyIi.BETWEEN_ONE_POINT]: '10',
		[EconomicSolvencyIi.LESS_ONE_POINT]: '0'
	};

	const recoveryTimeInYearsOptions = [
		{ value: RecoveryTimeInYears.LESS_FIVE_YEARS, label: '' },
		{ value: RecoveryTimeInYears.LESS_SIX_YEARS, label: '' },
		{ value: RecoveryTimeInYears.MORE_SIX_YEARS, label: '' },
		{ value: RecoveryTimeInYears.MORE_SEVEN_YEARS, label: '' }
	];

	const recoveryTimeInYearsPointsMap = {
		[RecoveryTimeInYears.LESS_FIVE_YEARS]: '15',
		[RecoveryTimeInYears.LESS_SIX_YEARS]: '13',
		[RecoveryTimeInYears.MORE_SIX_YEARS]: '10',
		[RecoveryTimeInYears.MORE_SEVEN_YEARS]: '5'
	};

	const getTotal = () => {
		const paymentExperienceOneTotal =
			parseInt(
				paymentExperienceOnePointsMap[
					paymentExperienceOneSelectedValue as ApplicantPaymentExperienceI
				]
			) || 0;
		const paymentExperienceTwoSolicitorTotal =
			parseInt(
				paymentExperienceTwoPointsMap[
					paymentExperienceTwoSolicitorSelectedValue as ApplicantPaymentExperienceIi
				]
			) || 0;
		const paymentExperienceTwoFamilyTotal =
			parseInt(
				paymentExperienceTwoPointsMap[
					paymentExperienceTwoFamilySelectedValue as ApplicantPaymentExperienceIi
				]
			) || 0;
		const economicSolvencyITotal =
			parseInt(economicSolvencyIPointsMap[economicSolvencyISelectedValue as EconomicSolvencyI]) ||
			0;
		const economicSolvencyIiTotal =
			parseInt(
				economicSolvencyIiPointsMap[economicSolvencyIiSelectedValue as EconomicSolvencyIi]
			) || 0;
		const recoveryTimeInYearsTotal =
			parseInt(
				recoveryTimeInYearsPointsMap[recoveryTimeInYearsSelectedValue as RecoveryTimeInYears]
			) || 0;

		return (
			paymentExperienceOneTotal +
			paymentExperienceTwoSolicitorTotal +
			paymentExperienceTwoFamilyTotal +
			economicSolvencyITotal +
			economicSolvencyIiTotal +
			recoveryTimeInYearsTotal
		);
	};

	const handleSaveMatrix = async () => {
		const input: FillEvaluationMatrixInput = {
			requestId: requestId,
			applicantPaymentExperienceI: paymentExperienceOneSelectedValue as ApplicantPaymentExperienceI,
			applicantPaymentExperienceIi:
				paymentExperienceTwoSolicitorSelectedValue as ApplicantPaymentExperienceIi,
			familyMemberPaymentExperienceIi:
				paymentExperienceTwoFamilySelectedValue as FamilyMemberPaymentExperienceIi,
			economicSolvencyI: economicSolvencyISelectedValue as EconomicSolvencyI,
			economicSolvencyIi: economicSolvencyIiSelectedValue as EconomicSolvencyIi,
			recoveryTimeInYears: recoveryTimeInYearsSelectedValue as RecoveryTimeInYears
		};

		await fillEvaluationMatrix({
			variables: { input }
		});
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	const isSaveButtonDisabled =
		!paymentExperienceOneSelectedValue ||
		!paymentExperienceTwoSolicitorSelectedValue ||
		!paymentExperienceTwoFamilySelectedValue ||
		!economicSolvencyISelectedValue ||
		!economicSolvencyIiSelectedValue ||
		!recoveryTimeInYearsSelectedValue;

	return (
		<>
			<div className="p-6">
				<div className="flex flex-col gap-4 p-4 bg-white rounded-lg w-full">
					<div className="flex flex-row items-center mb-4 justify-between">
						<div className="grow">
							<Typography weight="semibold">{t('EvaluationMatrix')}</Typography>
						</div>
					</div>
					<div className="flex flex-col">
						<div
							className="flex flex-row items-center border-b-2 gap-3"
							style={{ borderColor: BRAND_COLORS.brand600 }}
						>
							{headers.map((header, index) => (
								<div key={index} className={`flex-1 flex justify-center`}>
									{header && (
										<Typography weight="semibold">{t(toTranslationKey(header))}</Typography>
									)}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-4 border-b-2">
							{paymentExperienceOneRow.map((entry, index) => (
								<div
									key={index}
									className={`flex-1 flex flex-col gap-1 ${index === 0 ? 'text-left' : 'text-center'}`}
								>
									{entry.label && <Typography>{t(toTranslationKey(entry.label))}</Typography>}
									{entry.points && <Typography>{t(toTranslationKey(entry.points))}</Typography>}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-2 border-b-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('Solicitor')}</Typography>
							</div>
							<div className="flex-1 flex"></div>
							<CheckboxGroup
								name="payment-experience-one"
								options={paymentExperienceOneOptions}
								value={paymentExperienceOneSelectedValue}
								onChange={(value) => setPaymentExperienceOneSelectedValue(value)}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{
										paymentExperienceOnePointsMap[
											paymentExperienceOneSelectedValue as ApplicantPaymentExperienceI
										]
									}
								</Typography>
							</div>
						</div>
						<div className="flex flex-row items-center gap-3 py-4 border-b-2">
							{paymentExperienceTwoRow.map((entry, index) => (
								<div
									key={index}
									className={`flex-1 flex flex-col gap-1 ${index === 0 ? 'text-left' : 'text-center'}`}
								>
									{entry.label && <Typography>{t(toTranslationKey(entry.label))}</Typography>}
									{entry.points && <Typography>{t(toTranslationKey(entry.points))}</Typography>}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-2 border-b-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('Solicitor')}</Typography>
							</div>
							<div className="flex-1 flex"></div>
							<CheckboxGroup
								name="payment-experience-two-solicitor"
								options={paymentExperienceTwoOptions}
								value={paymentExperienceTwoSolicitorSelectedValue}
								onChange={(value) => setPaymentExperienceTwoSolicitorSelectedValue(value)}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{
										paymentExperienceTwoPointsMap[
											paymentExperienceTwoSolicitorSelectedValue as ApplicantPaymentExperienceIi
										]
									}
								</Typography>
							</div>
						</div>
						<div className="flex flex-row items-center gap-3 py-2 border-b-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('FamilyMember')}</Typography>
							</div>
							<div className="flex-1 flex"></div>
							<CheckboxGroup
								name="payment-experience-two-family"
								options={paymentExperienceTwoOptions}
								value={paymentExperienceTwoFamilySelectedValue}
								onChange={(value) => {
									setPaymentExperienceTwoFamilySelectedValue(value);
								}}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{
										paymentExperienceTwoPointsMap[
											paymentExperienceTwoFamilySelectedValue as ApplicantPaymentExperienceIi
										]
									}
								</Typography>
							</div>
						</div>
						<div className="flex flex-row items-center gap-3 py-4 border-b-2">
							{economicSolvencyIRow.map((entry, index) => (
								<div
									key={index}
									className={`flex-1 flex flex-col gap-1 ${index === 0 ? 'text-left' : 'text-center'}`}
								>
									{entry.label && <Typography>{t(toTranslationKey(entry.label))}</Typography>}
									{entry.points && <Typography>{t(toTranslationKey(entry.points))}</Typography>}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-2 border-b-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('Solicitor')}</Typography>
							</div>
							<div className="flex-1 flex justify-center gap-2 items-center">
								<Typography color="subtitle">{requestData?.economicSolvencyI || '-'}</Typography>
								<Info
									color={BRAND_COLORS.brand600}
									size={18}
									onClick={() => {
										setOpenInformationModal(true);
										setInformationModalContent('EconomicSolvencyOne');
									}}
									className="cursor-pointer"
								/>
							</div>
							<CheckboxGroup
								name="economic-solvency-one"
								options={economicSolvencyIOptions}
								value={economicSolvencyISelectedValue}
								onChange={(value) => setEconomicSolvencyISelectedValue(value)}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{economicSolvencyIPointsMap[economicSolvencyISelectedValue as EconomicSolvencyI]}
								</Typography>
							</div>
						</div>
						<div className="flex flex-row items-center gap-3 py-4 border-b-2">
							{economicSolvencyIiRow.map((entry, index) => (
								<div
									key={index}
									className={`flex-1 flex flex-col gap-1 ${index === 0 ? 'text-left' : 'text-center'}`}
								>
									{entry.label && <Typography>{t(toTranslationKey(entry.label))}</Typography>}
									{entry.points && <Typography>{t(toTranslationKey(entry.points))}</Typography>}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-2 border-b-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('Solicitor')}</Typography>
							</div>
							<div className="flex-1 flex justify-center gap-2 items-center">
								<Typography color="subtitle">{economicSolvencyIi}</Typography>
								<Info
									color={BRAND_COLORS.brand600}
									size={18}
									onClick={() => {
										setOpenInformationModal(true);
										setInformationModalContent('EconomicSolvency');
									}}
									className="cursor-pointer"
								/>
							</div>
							<CheckboxGroup
								name="economic-solvency-two"
								options={economicSolvencyIiOptions}
								value={economicSolvencyIiSelectedValue}
								onChange={(value) => setEconomicSolvencyIiSelectedValue(value)}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{
										economicSolvencyIiPointsMap[
											economicSolvencyIiSelectedValue as EconomicSolvencyIi
										]
									}
								</Typography>
							</div>
						</div>
						<div className="flex flex-row items-center gap-3 py-4 border-b-2">
							{recoveryTimeInYearsRow.map((entry, index) => (
								<div
									key={index}
									className={`flex-1 flex flex-col gap-1 ${index === 0 ? 'text-left' : 'text-center'}`}
								>
									{entry.label && <Typography>{t(toTranslationKey(entry.label))}</Typography>}
									{entry.points && <Typography>{t(toTranslationKey(entry.points))}</Typography>}
								</div>
							))}
						</div>
						<div className="flex flex-row items-center gap-3 py-2">
							<div className="flex-1 flex">
								<Typography color="subtitle">{t('Solicitor')}</Typography>
							</div>
							<div className="flex-1 flex justify-center gap-2 items-center">
								<Typography color="subtitle">{recoveryTimeInYears}</Typography>
								<Info
									color={BRAND_COLORS.brand600}
									size={18}
									onClick={() => {
										setOpenInformationModal(true);
										setInformationModalContent('RecuperationTime');
									}}
									className="cursor-pointer"
								/>
							</div>
							<CheckboxGroup
								name="recovery-time-in-years"
								options={recoveryTimeInYearsOptions}
								value={recoveryTimeInYearsSelectedValue}
								onChange={(value) => {
									setRecoveryTimeInYearsSelectedValue(value);
								}}
								checkboxStyle="rounded"
								customCheckboxStyles="flex-1 flex items-center justify-center"
								disabled={inCreditCover}
							/>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold">
									{
										recoveryTimeInYearsPointsMap[
											recoveryTimeInYearsSelectedValue as RecoveryTimeInYears
										]
									}
								</Typography>
							</div>
						</div>
						<div
							className="flex flex-row items-center gap-3 py-2 border-t-2"
							style={{ borderColor: BRAND_COLORS.brand600 }}
						>
							<div className="flex-1 flex">
								<Typography weight="semibold" size="lg">
									{t('Total')}
								</Typography>
							</div>
							<div className="flex-1 flex"></div>
							<div className="flex-1 flex"></div>
							<div className="flex-1 flex"></div>
							<div className="flex-1 flex"></div>
							<div className="flex-1 flex"></div>
							<div className="flex-1 flex justify-center">
								<Typography weight="semibold" size="lg">
									{getTotal()}
								</Typography>
							</div>
						</div>
						<div className={`flex justify-between ${inCreditCover ? 'hidden' : ''}`}>
							<TextBadge
								text={t('RememberToSaveChanges')}
								icon={<AlertTriangle color={TEXT_COLORS.warning} />}
								type={'warning'}
							/>

							<Tooltip content={isSaveButtonDisabled ? t('DisabledSaveMatrixTooltip') : ''}>
								<Button
									size="auto"
									label={requestData?.evaluationMatrix ? t('UpdateMatrix') : t('SaveMatrix')}
									onClick={handleSaveMatrix}
									disabled={isSaveButtonDisabled}
								/>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
			<Modal
				isOpen={openInformationModal}
				onClose={() => {
					setOpenInformationModal(false);
				}}
				closeable
				maxWidth={800}
			>
				<div className="flex flex-col gap-4">
					<div className="flex">
						<Typography weight="semibold">
							{t(toTranslationKey(informationModalContent))}
						</Typography>
					</div>
					<div className="flex flex-col border-y-2 py-4">
						<Typography>
							{informationModalContent === 'EconomicSolvency'
								? t('EconomicSolvencyTwoContent')
								: informationModalContent === 'EconomicSolvencyOne'
									? t('EconomicSolvencyOneContent')
									: t('RecuperationTimeContent')}
						</Typography>
						<div className="border-t-2 border-gray-300 pt-4">
							{informationModalContent === 'EconomicSolvency' ? (
								<TextSectionWithFormula
									text={requestData?.economicSolvencyIi || '-'}
									title="EconomicSolvencyTwo"
									numerator="EconomicSolvencyTwoNumerator"
									denominator="EconomicSolvencyTwoDenominator"
									t={tForComponent(t)}
									showTitle={false}
								/>
							) : informationModalContent === 'EconomicSolvencyOne' ? (
								<TextSectionWithFormula
									text={requestData?.economicSolvencyI || '-'}
									title="EconomicSolvencyOne"
									numerator="EconomicSolvencyOneNumerator"
									denominator="EconomicSolvencyOneDenominator"
									t={tForComponent(t)}
									showTitle={false}
								/>
							) : (
								<TextSectionWithFormula
									text={requestData?.recoveryTimeInYears || '-'}
									title="RecuperationTime"
									numerator="RecuperationTimeNumerator"
									denominator="RecuperationTimeDenominator"
									t={tForComponent(t)}
									showTitle={false}
								/>
							)}
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default EvaluationMatrix;
